/**
 * App.jsx - Root component.
 *
 * Public routes (no auth required):
 *   /          → PublicExamsPage (exam listing)
 *   /login     → LoginPage
 *   /register  → RegisterPage
 *
 * Authenticated routes (student/guest):
 *   /dashboard          → HomePage (student dashboard)
 *   /settings           → SettingsPage
 *   /exam/:id           → ExamPage (standalone layout)
 */

import { useState, useEffect } from 'react'
import { BrowserRouter, Route, Routes, Navigate, Outlet } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/context/AuthContext'
import { ThemeProvider }  from '@/context/ThemeContext'
import AuthGate           from '@/components/ui/AuthGate'
import { telegramOidcLogin, linkTelegramAccount } from '@/api/auth'
import { hasTelegramOidcCallback, consumeTelegramOidcCallback } from '@/utils/telegramOidc'
import DashboardLayout    from '@/layouts/DashboardLayout'
import PublicLayout       from '@/layouts/PublicLayout'
import LandingPage        from '@/pages/Landing/LandingPage'
import PublicExamsPage    from '@/pages/PublicExams/PublicExamsPage'
import LoginPage          from '@/pages/Auth/LoginPage'
import RegisterPage       from '@/pages/Auth/RegisterPage'
import HomePage           from '@/pages/Home/HomePage'
import SettingsPage       from '@/pages/Settings/SettingsPage'
import StatsPage          from '@/pages/Stats/StatsPage'
import TeachersPage       from '@/pages/Teachers/TeachersPage'
import TeacherDetailPage  from '@/pages/Teachers/TeacherDetailPage'
import HomeworkPage       from '@/pages/Homework/HomeworkPage'
import CalendarPage       from '@/pages/Calendar/CalendarPage'
import RatingPage         from '@/pages/Rating/RatingPage'
import ExamPage           from '@/pages/Exam/ExamPage'
import ExamResultPage     from '@/pages/Exam/ExamResultPage'
import LeaderboardPage    from '@/pages/Exam/LeaderboardPage'
import JoinExamPage       from '@/pages/JoinExamPage'
import PrivacyPage        from '@/pages/Privacy/PrivacyPage'
import DeleteAccountPage  from '@/pages/DeleteAccount/DeleteAccountPage'

/** Guard: only for authenticated non-guest users */
function RequireAuth() {
  const { isAuth, isLoading, user } = useAuth()
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-slate-950">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }
  if (!isAuth || user?.role === 'guest') return <Navigate to="/" replace />
  return <Outlet />
}

function useIsDashboardUser() {
  const { isAuth, isLoading, user } = useAuth()
  return { isLoading, isDashboardUser: isAuth && user?.role !== 'guest' }
}

/** "/" — dashboard users land on /dashboard; everyone else sees the landing page. */
function RootRoute() {
  const { isLoading, isDashboardUser } = useIsDashboardUser()
  if (isLoading) return null
  if (isDashboardUser) return <Navigate to="/dashboard" replace />
  return (
    <PublicLayout>
      <LandingPage />
    </PublicLayout>
  )
}

/** "/exams" — same page for everyone, but dashboard users keep the sidebar shell. */
function ExamsRoute() {
  const { isLoading, isDashboardUser } = useIsDashboardUser()
  if (isLoading) return null
  return isDashboardUser ? (
    <DashboardLayout>
      <PublicExamsPage />
    </DashboardLayout>
  ) : (
    <PublicLayout>
      <PublicExamsPage />
    </PublicLayout>
  )
}

// Intercepts the "Log in with Telegram" OIDC redirect (registered as the app
// root, e.g. https://5mi.uz/) and exchanges the code before normal routing
// (and AuthGate's Telegram WebApp check) render anything.
function TelegramOidcGate({ children }) {
  const { login, refreshUser } = useAuth()
  const [status, setStatus] = useState(() => hasTelegramOidcCallback() ? 'processing' : 'idle')

  useEffect(() => {
    if (status !== 'processing') return
    const callback = consumeTelegramOidcCallback()
    if (!callback) { setStatus('idle'); return }

    // 'link' mode: attach Telegram to the already-authenticated account
    // (started from Settings) instead of signing in as a new/matched user.
    if (callback.mode === 'link') {
      linkTelegramAccount(callback.code, `${window.location.origin}/`, callback.verifier)
        .then(() => refreshUser())
        .catch((err) => {
          const detail = err.response?.data?.detail
          sessionStorage.setItem('tg_link_error', detail || "Telegram hisobini bog'lash muvaffaqiyatsiz")
        })
        .finally(() => window.location.replace(callback.returnPath))
      return
    }

    telegramOidcLogin(callback.code, `${window.location.origin}/`, callback.verifier)
      .then(({ data }) => {
        login({ access: data.access, refresh: data.refresh, user: data.user })
        window.location.replace('/')
      })
      .catch((err) => {
        const detail = err.response?.data?.detail
        sessionStorage.setItem('tg_oidc_error', detail || 'Telegram orqali kirish muvaffaqiyatsiz')
        setStatus('idle')
      })
  }, [status])

  if (status === 'processing') {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-slate-950">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }
  return children
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TelegramOidcGate>
        <BrowserRouter>
          <AuthGate>
          <Routes>
            {/* ── Root + exams: layout depends on auth state ──── */}
            <Route path="/"      element={<RootRoute />} />
            <Route path="/exams" element={<ExamsRoute />} />

            {/* ── Public routes (with shared header) ────────── */}
            <Route element={<PublicLayout />}>
              <Route path="/login"    element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/privacy"  element={<PrivacyPage />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              <Route path="/exam/:code/result"      element={<ExamResultPage />} />
              <Route path="/exam/:code/leaderboard" element={<LeaderboardPage />} />
            </Route>

            {/* ── Exam taking (standalone full-screen layout) ── */}
            <Route path="/exam/:code" element={<ExamPage />} />

            {/* ── Join by code (Telegram bot start_param) ─────── */}
            <Route path="/join/:code" element={<JoinExamPage />} />

            {/* ── Authenticated student dashboard ────────────── */}
            <Route element={<RequireAuth />}>
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard"   element={<HomePage />} />
                <Route path="/calendar"    element={<CalendarPage />} />
                <Route path="/homework"    element={<HomeworkPage />} />
                <Route path="/results"     element={<StatsPage />} />
                <Route path="/teachers"    element={<TeachersPage />} />
                <Route path="/teachers/:id" element={<TeacherDetailPage />} />
                <Route path="/rating"      element={<RatingPage />} />
                <Route path="/settings"    element={<SettingsPage />} />
              </Route>
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </AuthGate>
        </BrowserRouter>
        </TelegramOidcGate>
      </AuthProvider>
    </ThemeProvider>
  )
}
